import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Users, AlertTriangle } from 'lucide-react';
import { getTeamCalendar } from '../../api/Attendance/leaves';
import { TeamCalendarView, CalendarEntry, TeamMemberInfo } from '../../models/attendance';
import { getFullUrl } from '../../utils/url';
import { resolveCoverageLevel } from './CoverageIndicator';
import { cn } from '../../utils/cn';
import { appDate, appWeekday } from '../../utils/appDateFormat';
import { formatLocalDateParam, parseApiDateOnly } from '@/utils/apiDate';

// --- Helpers ---

function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// Local calendar day — toISOString() is the UTC day and shifts the API range a day east of UTC.
const formatDateParam = (date: Date): string => formatLocalDateParam(date);

const DEFAULT_NON_WORKING_DAYS = [5, 6];

function getLeaveColor(leaveTypeColor?: string): {
  bg: string;
  bgStyle?: React.CSSProperties;
} {
  const color = (leaveTypeColor || '').trim();
  if (color.startsWith('#')) {
    return { bg: '', bgStyle: { backgroundColor: `${color}30` } };
  }
  const colorMap: Record<string, string> = {
    green: 'bg-emerald-200',
    blue: 'bg-blue-200',
    red: 'bg-red-200',
    orange: 'bg-orange-200',
    purple: 'bg-purple-200',
    pink: 'bg-pink-200',
    gray: 'bg-gray-200',
  };
  return { bg: colorMap[(color || 'blue').toLowerCase()] || 'bg-blue-200' };
}

// --- Props ---

export interface MiniTeamCalendarProps {
  /** Start date of the leave request */
  leaveStartDate: string;
  /** End date of the leave request */
  leaveEndDate: string;
  /** Optional className for root element */
  className?: string;
}

// --- Component ---

export function MiniTeamCalendar({
  leaveStartDate,
  leaveEndDate,
  className,
}: MiniTeamCalendarProps) {
  const { t } = useTranslation('leaves');
  const [calendarData, setCalendarData] = useState<TeamCalendarView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Compute a 2-week window centered on the leave dates
  const dateRange = useMemo(() => {
    const start = parseApiDateOnly(leaveStartDate) ?? new Date(NaN);
    const end = parseApiDateOnly(leaveEndDate) ?? new Date(NaN);

    const leaveDays = Math.round(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;

    // If leave is <= 7 days, show 7 days before + leave + 7 days after (capped at 14 days)
    // If leave is > 7 days, show leave range + 3 days padding each side (capped at 21 days)
    let windowStart: Date;
    let windowEnd: Date;

    if (leaveDays <= 7) {
      // Center 14-day window on the leave midpoint
      const midpoint = new Date(start.getTime() + (end.getTime() - start.getTime()) / 2);
      windowStart = addDays(midpoint, -6);
      windowEnd = addDays(midpoint, 7);
    } else {
      windowStart = addDays(start, -3);
      windowEnd = addDays(end, 3);
    }

    // Snap to Monday start
    const dayOfWeek = windowStart.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    windowStart = addDays(windowStart, mondayOffset);

    // Ensure at least 14 days
    const totalDays = Math.round(
      (windowEnd.getTime() - windowStart.getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;
    if (totalDays < 14) {
      windowEnd = addDays(windowStart, 13);
    }

    return { from: windowStart, to: windowEnd };
  }, [leaveStartDate, leaveEndDate]);

  // Generate day array
  const days = useMemo(() => {
    const result: Date[] = [];
    let d = new Date(dateRange.from);
    while (d <= dateRange.to) {
      result.push(new Date(d));
      d = addDays(d, 1);
    }
    return result;
  }, [dateRange]);

  // Non-working day detection from API data (derived from work schedules)
  const nonWorkingDaysSet = useMemo(() => {
    const days = calendarData?.nonWorkingDays ?? DEFAULT_NON_WORKING_DAYS;
    return new Set(days);
  }, [calendarData?.nonWorkingDays]);

  const isNonWorkingDay = useCallback(
    (date: Date): boolean => nonWorkingDaysSet.has(date.getDay()),
    [nonWorkingDaysSet]
  );

  // Is this day within the leave request range?
  const isLeaveDay = useCallback(
    (date: Date): boolean => {
      const start = parseApiDateOnly(leaveStartDate) ?? new Date(NaN);
      const end = parseApiDateOnly(leaveEndDate) ?? new Date(NaN);
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d >= start && d <= end;
    },
    [leaveStartDate, leaveEndDate]
  );

  // Fetch data
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await getTeamCalendar(
          formatDateParam(dateRange.from),
          formatDateParam(dateRange.to),
          { pageSize: 10 }
        );
        if (!cancelled && res.errors.length === 0) {
          setCalendarData(res.payload);
        }
      } catch {
        if (!cancelled) setError(true);
      }
      if (!cancelled) setLoading(false);
    };
    fetchData();
    return () => { cancelled = true; };
  }, [dateRange]);

  // Pre-parse entry dates once and group by userId
  type ParsedEntry = CalendarEntry & { _start: number; _end: number };
  const memberEntries = useMemo(() => {
    if (!calendarData) return new Map<string, ParsedEntry[]>();
    const map = new Map<string, ParsedEntry[]>();
    for (const entry of calendarData.entries) {
      const s = new Date(entry.startDate); s.setHours(0, 0, 0, 0);
      const e = new Date(entry.endDate); e.setHours(0, 0, 0, 0);
      const parsed: ParsedEntry = { ...entry, _start: s.getTime(), _end: e.getTime() };
      if (!map.has(entry.userId)) map.set(entry.userId, []);
      map.get(entry.userId)!.push(parsed);
    }
    return map;
  }, [calendarData]);

  // Members with entries
  const members = useMemo((): (TeamMemberInfo & { entries: ParsedEntry[] })[] => {
    if (!calendarData?.members?.length) {
      const seen = new Map<string, TeamMemberInfo & { entries: ParsedEntry[] }>();
      for (const [userId, entries] of memberEntries) {
        const first = entries[0];
        if (first && !seen.has(userId)) {
          seen.set(userId, {
            userId,
            userName: first.userName,
            imageUrl: first.imageUrl,
            entries,
          });
        }
      }
      return Array.from(seen.values()).sort((a, b) =>
        a.userName.localeCompare(b.userName)
      );
    }
    return calendarData.members.map((m) => ({
      ...m,
      entries: memberEntries.get(m.userId) || [],
    })).filter(m => m.entries.length > 0);
  }, [calendarData, memberEntries]);

  const totalMembers = calendarData?.totalMembers || members.length || 1;

  // Daily off counts (uses pre-parsed timestamps)
  const dailyOffCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const day of days) {
      const dateKey = formatDateParam(day);
      const dayTime = day.getTime();
      let count = 0;
      for (const member of members) {
        const isOff = member.entries.some((e) =>
          dayTime >= e._start && dayTime <= e._end && e.status === 'Approved'
        );
        if (isOff) count++;
      }
      counts.set(dateKey, count);
    }
    return counts;
  }, [days, members]);

  // Get leave entry for a member on a specific day (uses pre-parsed timestamps)
  const getMemberLeaveForDay = (
    entries: ParsedEntry[],
    day: Date
  ): ParsedEntry | undefined => {
    const dayTime = day.getTime();
    return entries.find((e) => dayTime >= e._start && dayTime <= e._end);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (error) {
    return (
      <div
        className={cn('flex items-center gap-2 p-3 text-xs text-muted-foreground', className)}
        data-testid="mini-calendar-error"
      >
        <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
        {t('miniCalendarError', 'Could not load team calendar')}
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={cn('flex items-center justify-center py-4', className)}
        data-testid="mini-calendar-loading"
      >
        <Loader2 size={18} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div
        className={cn('flex items-center gap-2 p-3 text-xs text-muted-foreground', className)}
        data-testid="mini-calendar-empty"
      >
        <Users size={14} className="flex-shrink-0" />
        {t('miniCalendarNoData', 'No team data available')}
      </div>
    );
  }

  return (
    <div
      className={cn('overflow-hidden', className)}
      data-testid="mini-team-calendar"
      role="region"
      aria-label={t('miniCalendarAriaLabel', 'Team availability calendar')}
    >
      {/* Compact header showing date range */}
      <div className="flex items-center justify-between px-2 py-1.5 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <Users size={11} />
          {t('miniCalendarTeamMembers', '{{count}} team members', { count: members.length })}
        </span>
        <span>
          {appDate(dateRange.from)}
          {' — '}
          {appDate(dateRange.to)}
        </span>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[10px]" data-testid="mini-calendar-grid">
          <thead>
            <tr>
              <th className="sticky ltr:left-0 rtl:right-0 z-10 bg-muted/50 border-b ltr:border-r rtl:border-l border-border px-1.5 py-1 text-start text-[9px] font-medium text-muted-foreground min-w-[100px]" />
              {days.map((day) => {
                const dateKey = formatDateParam(day);
                const offCount = dailyOffCounts.get(dateKey) || 0;
                const isToday = isSameDay(day, today);
                const nonWorkingDay = isNonWorkingDay(day);
                const inLeaveRange = isLeaveDay(new Date(day));

                return (
                  <th
                    key={dateKey}
                    className={cn(
                      'border-b border-r border-border px-0.5 py-1 text-center min-w-[28px]',
                      nonWorkingDay && 'bg-muted/30',
                      isToday && 'ring-1 ring-inset ring-blue-500',
                      inLeaveRange && 'bg-blue-50'
                    )}
                  >
                    <div className="text-[8px] font-medium text-muted-foreground uppercase leading-none">
                      {appWeekday(day, 'narrow')}
                    </div>
                    <div
                      className={cn(
                        'text-[10px] font-semibold leading-tight',
                        isToday ? 'text-blue-600' : 'text-foreground'
                      )}
                    >
                      {day.getDate()}
                    </div>
                    {offCount > 0 && (
                      <div
                        className={cn(
                          'mx-auto mt-0.5 w-1.5 h-1.5 rounded-full',
                          resolveCoverageLevel(totalMembers, offCount) === 'critical' && 'bg-red-500',
                          resolveCoverageLevel(totalMembers, offCount) === 'low' && 'bg-yellow-500',
                          resolveCoverageLevel(totalMembers, offCount) === 'ok' && 'bg-green-500'
                        )}
                        title={`${offCount} ${t('off')}`}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {members.slice(0, 10).map((member) => (
              <tr
                key={member.userId}
                className="hover:bg-muted/10"
                data-testid="mini-calendar-member-row"
              >
                {/* Member name */}
                <td className="sticky ltr:left-0 rtl:right-0 z-10 bg-card border-b ltr:border-r rtl:border-l border-border px-1.5 py-0.5">
                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center flex-shrink-0 text-white text-[8px] font-medium overflow-hidden">
                      {getFullUrl(member.imageUrl) ? (
                        <img
                          src={getFullUrl(member.imageUrl)!}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        (member.userName || '?').charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="text-[10px] font-medium text-foreground truncate max-w-[70px]">
                      {member.userName}
                    </span>
                  </div>
                </td>

                {/* Day cells */}
                {days.map((day) => {
                  const dateKey = formatDateParam(day);
                  const leave = getMemberLeaveForDay(member.entries, day);
                  const nonWorkingDay = isNonWorkingDay(day);
                  const isToday = isSameDay(day, today);
                  const inLeaveRange = isLeaveDay(new Date(day));

                  if (leave) {
                    const colors = getLeaveColor(leave.leaveTypeColor);
                    const isPending = leave.status === 'Pending';

                    return (
                      <td
                        key={dateKey}
                        className={cn(
                          'border-b border-r border-border px-0 py-0',
                          isToday && 'ring-1 ring-inset ring-blue-500'
                        )}
                        title={`${member.userName}: ${leave.leaveType} (${leave.status})`}
                      >
                        <div
                          className={cn(
                            'w-full min-h-[22px] flex items-center justify-center',
                            colors.bg,
                            isPending && 'opacity-60'
                          )}
                          style={colors.bgStyle}
                        >
                          <span
                            className="text-[8px] font-medium text-black truncate px-0.5"
                          >
                            {leave.leaveType.substring(0, 2)}
                          </span>
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={dateKey}
                      className={cn(
                        'border-b border-r border-border',
                        nonWorkingDay && 'bg-muted/20',
                        isToday && 'ring-1 ring-inset ring-blue-500',
                        inLeaveRange && 'bg-blue-50/50'
                      )}
                    >
                      <div className="min-h-[22px]" />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Overflow note */}
      {members.length > 10 && (
        <div className="px-2 py-1 text-[9px] text-muted-foreground text-end border-t border-border">
          {t('miniCalendarMoreMembers', '+{{count}} more members', {
            count: members.length - 10,
          })}
        </div>
      )}
    </div>
  );
}
